import { Component, DestroyRef, inject, OnInit, viewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import {
    NucButtonSecondaryComponent,
    NucDialogCustomContentConfig,
    NucDialogCustomContentService,
    NucDropdownComponent,
    NucDropdownItem,
    NucFullModalConfig,
    NucFullModalService,
    NucIconButtonComponent,
    NucProgressBarComponent,
    NucStatusBarComponent
} from '@relayter/rubber-duck';
import { ChartData, ChartOptions, ChartType } from 'chart.js';
import { BaseChartDirective, NgChartsModule } from 'ng2-charts';
import { filter, Subscription } from 'rxjs';
import { FileSizePipe } from 'src/app/pipes/filesize.pipe';
import { EStatisticsCollections, EStatisticsPeriod, StatisticsService } from '../../../api/services/statistics.service';
import { TeamService } from '../../../api/services/team.service';
import { AppConstants } from '../../../app.constants';
import { TransferOwnershipFormComponent } from '../../../forms/transfer-ownership-form/transfer-ownership-form.component';
import { DropdownOption } from '../../../models/dropdown-option.model';
import { RATE_LIMIT_GROUPS, RateLimitModel, TeamModel } from '../../../models/response/team.model';
import { RLDatePipe } from '../../../pipes/date.pipe';
import { EditTeamRateLimitsComponent, IEditTeamRateLimitData } from '../edit-team-rate-limts/edit-team-rate-limits.component';
import { EditTeamUsageComponent, IEditTeamUsageData } from '../edit-team-usage/edit-team-usage.component';

export class StatisticsForm {
    period: FormControl<EStatisticsPeriod>;
    collection: FormControl<EStatisticsCollections>;
}

@Component({
    selector: 'om-team-detail',
    templateUrl: './team-detail.component.html',
    styleUrls: ['./team-detail.component.scss'],
    imports: [
        RLDatePipe,
        FileSizePipe,
        ReactiveFormsModule,
        NucDropdownComponent,
        NgChartsModule,
        NucIconButtonComponent,
        NucButtonSecondaryComponent,
        NucStatusBarComponent,
        NucProgressBarComponent
    ]
})
export class TeamDetailComponent implements OnInit {
    private readonly chart = viewChild(BaseChartDirective);

    private readonly destroyRef = inject(DestroyRef);
    private readonly route = inject(ActivatedRoute);
    private readonly teamService = inject(TeamService);
    private readonly fullModalService = inject(NucFullModalService);
    private readonly statisticsService = inject(StatisticsService);
    private readonly dialogCustomContentService = inject(NucDialogCustomContentService);

    protected readonly RATE_LIMIT_GROUPS = RATE_LIMIT_GROUPS;

    public team: TeamModel;
    public dateFormats = RLDatePipe.dateFormats;

    public barChartOptions: ChartOptions = {
        responsive: false,
        maintainAspectRatio: false,
        animation: { duration: 0 }
    };

    public barChartType: ChartType = 'bar';
    public barChartLegend = false;
    public barChartData: ChartData = {
        datasets: [
            {
                data: [],
                label: 'Total material created',
                backgroundColor: AppConstants.FIRST_BRAND_COLOR,
                hoverBackgroundColor: AppConstants.FIRST_BRAND_COLOR
            }
        ]
    };

    public periods: DropdownOption[] = Object.values(EStatisticsPeriod).map((period) => new DropdownOption(period, period));

    public collections: DropdownOption[] = Object.values(EStatisticsCollections).map((collection) => new DropdownOption(collection, collection));

    public formGroupStatistics: FormGroup<StatisticsForm>;

    private teamSubscription: Subscription;

    public rateLimitGroups: NucDropdownItem[] = Object.entries(RATE_LIMIT_GROUPS).map(([key, label]) => new NucDropdownItem(label, key));

    public selectedRateLimit: RateLimitModel;
    public selectedRateLimitGroup = RATE_LIMIT_GROUPS.USER;

    public ngOnInit(): void {
        this.getTeam();

        this.formGroupStatistics = new FormGroup<StatisticsForm>({
            period: new FormControl(null),
            collection: new FormControl(null)
        });

        this.formGroupStatistics.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
            this.getStatistics(value.period, value.collection);
        });

        this.formGroupStatistics.patchValue({
            period: EStatisticsPeriod.MONTH,
            collection: EStatisticsCollections.RLPublicationItem
        });
    }

    private getStatistics(period, collection): void {
        const teamId = this.route.snapshot.params.teamId;
        this.statisticsService.getPeriodTotals(teamId, period, collection).subscribe((results: any) => {
            this.barChartData.labels = results.items.map((item) => item.getPeriod());
            this.barChartData.datasets[0].data = results.items.map((item) => item.value);
            this.chart()?.update();
        });
    }

    public onEditTeamUsageClicked(): void {
        const config = new NucFullModalConfig('Team usage', 'Set up the team usage', { team: this.team } as IEditTeamUsageData);
        config.confirmClose = true;
        this.fullModalService
            .open(EditTeamUsageComponent, config)
            .afterClosed()
            .pipe(filter((result) => !!result))
            .subscribe(() => this.getTeam());
    }

    private getTeam(): void {
        if (this.teamSubscription && !this.teamSubscription.closed) this.teamSubscription.unsubscribe();
        this.teamSubscription = this.teamService.getTeam(this.route.snapshot.params.teamId).subscribe((team) => {
            this.team = team;
        });
    }

    public openTransferOwnershipDialog(): void {
        const dialogConfig = new NucDialogCustomContentConfig('Transfer ownership', 'Select an existing user within the team to transfer the ownership to', {
            team: this.team
        });
        this.dialogCustomContentService
            .open(TransferOwnershipFormComponent, dialogConfig)
            .afterClosed()
            .pipe(filter((result) => !!result))
            .subscribe(() => this.getTeam());
    }

    public onRateLimitClicked(): void {
        const config = new NucFullModalConfig('Team Rate limits', 'Set up the team rate limits', {
            team: this.team,
            rateLimit: this.selectedRateLimit
        } as IEditTeamRateLimitData);
        config.confirmClose = true;
        this.fullModalService
            .open(EditTeamRateLimitsComponent, config)
            .afterClosed()
            .pipe(filter((result) => !!result))
            .subscribe(() => {
                this.getTeam();
                this.rateLimitGroupDropDownChanged(this.selectedRateLimitGroup);
            });
    }

    public rateLimitGroupDropDownChanged(group): void {
        this.selectedRateLimit = this.team.rateLimits?.find((rateLimit) => rateLimit.group === group) || new RateLimitModel(group);
    }
}
